// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { chargeScheduleSchema } from '@/constants/charge-schedule';
import { chargeStatusSchema } from '@/constants/charge-status';
import { pricingModeSchema } from '@/constants/pricing-mode';
import { currencySchema } from '@/currency/api/types';
import { dateSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

export const attachableChargeSchema = z.object({
  chargeKey: z.string(),
  label: z.string(),
  status: chargeStatusSchema,
  pricingMode: pricingModeSchema,
  quantity: z.number(),
  unitPrice: currencySchema.nullable(),
  totalPrice: currencySchema,
  servicePeriodStart: dateSchema.nullable(),
  servicePeriodEnd: dateSchema.nullable(),
  expectedBillableDate: dateSchema.nullable(),
  billingEligibleDate: dateSchema.nullable(),
  lineItemKey: z.string(),
  lineItemSequence: z.number(),
  lineItemChargeSchedule: chargeScheduleSchema.nullable(),
  itemKey: z.string(),
  itemName: z.string(),
  itemSku: z.string(),
});

export type AttachableCharge = z.infer<typeof attachableChargeSchema>;

const attachableChargesResponseSchema = z.object({
  data: z.object({
    // Go nil slices serialize as JSON null. Coalesce to [] so the picker
    // renders an empty state instead of throwing on Zod parse.
    charges: z
      .array(attachableChargeSchema)
      .nullable()
      .transform((value) => value ?? []),
  }),
  metadata: responseMetadataSchema,
});

export async function getAttachableCharges(invoiceKey: string) {
  const url = `/v1/invoice/${invoiceKey}/attachable-charges`;
  const response = await api.get(url);
  const result = await parseResponse(
    attachableChargesResponseSchema,
    response.data,
    {
      schemaName: 'AttachableCharges',
      endpoint: url,
    }
  );
  return result.data.charges;
}
